import axios, { AxiosError, AxiosInstance } from "axios";
import { API_ERROR } from "../../constants/components-const";
import {
  GetNoticiasParams,
  GetResourcesParams,
  ProblemJson,
  PublicDetailResponse,
  PublicListResponse,
  PublicNodeDTO,
  PublicNoticiaDTO,
  PublicNoticiaDetailDTO,
  PublicOrganizationDTO,
  PublicResourceDTO,
  PublicResourceDetailDTO,
} from "../../types/api/cmsPublic";

const DEFAULT_TIMEOUT_MS = 12000;

export class CmsApiError extends Error {
  status: number;
  problem?: ProblemJson;

  constructor(message: string, status: number, problem?: ProblemJson) {
    super(message);
    this.name = "CmsApiError";
    this.status = status;
    this.problem = problem;
  }
}

const trimRightSlash = (value: string) => value.replace(/\/+$/, "");

export const resolveCmsBaseUrl = () => {
  const cmsUrl = process.env.REACT_APP_CMS_API_BASE_URL?.trim();
  if (cmsUrl) {
    return trimRightSlash(cmsUrl);
  }

  const fallback = process.env.REACT_APP_BASE_URL?.trim();
  if (fallback) {
    return `${trimRightSlash(fallback)}/public`;
  }

  return "";
};

export const toProblemJson = (
  error: AxiosError<ProblemJson>
): ProblemJson | undefined => {
  const contentType = String(error.response?.headers?.["content-type"] || "");
  const data = error.response?.data;

  if (!data) {
    return undefined;
  }

  const looksLikeProblem =
    typeof data === "object" &&
    typeof data.title === "string" &&
    typeof data.status === "number";

  if (contentType.includes("application/problem+json") || looksLikeProblem) {
    return {
      type: data.type,
      title: data.title,
      status: data.status,
      detail: data.detail,
      instance: data.instance,
      code: data.code,
      errors: Array.isArray(data.errors) ? data.errors : [],
    };
  }

  return undefined;
};

const buildCmsApiError = (error: unknown): CmsApiError => {
  const axiosGuard =
    typeof axios.isAxiosError === "function" ? axios.isAxiosError(error) : false;
  const isAxiosErrorLike =
    axiosGuard || Boolean((error as { isAxiosError?: boolean })?.isAxiosError);

  if (isAxiosErrorLike) {
    const axiosError = error as AxiosError<ProblemJson>;
    const problem = toProblemJson(axiosError);
    const status = axiosError.response?.status || problem?.status || 500;
    const message =
      problem?.detail || problem?.title || axiosError.message || API_ERROR;

    return new CmsApiError(message, status, problem);
  }

  return new CmsApiError(API_ERROR, 500);
};

export class CmsPublicApiClient {
  private readonly instance: AxiosInstance;

  constructor(baseURL = resolveCmsBaseUrl()) {
    this.instance = axios.create({
      baseURL,
      timeout: DEFAULT_TIMEOUT_MS,
      headers: {
        "Content-Type": "application/json",
      },
    });
  }

  private async get<R, P = Record<string, unknown>>(
    url: string,
    params?: P
  ): Promise<R> {
    try {
      const response = await this.instance.get<R>(url, { params });
      return response.data;
    } catch (error) {
      throw buildCmsApiError(error);
    }
  }

  getOrganizations(
    page = 1,
    pageSize = 20
  ): Promise<PublicListResponse<PublicOrganizationDTO>> {
    return this.get("/organizations", { page, pageSize });
  }

  getNodes(
    page = 1,
    pageSize = 20
  ): Promise<PublicListResponse<PublicNodeDTO>> {
    return this.get("/nodes", { page, pageSize });
  }

  getNodeBySlug(slug: string): Promise<PublicDetailResponse<PublicNodeDTO>> {
    return this.get(`/nodes/${encodeURIComponent(slug)}`);
  }

  getOrganizationNodesBySlug(
    slug: string
  ): Promise<PublicListResponse<PublicNodeDTO>> {
    return this.get(`/organizations/${encodeURIComponent(slug)}/nodes`);
  }

  getResources(
    params: GetResourcesParams = {}
  ): Promise<PublicListResponse<PublicResourceDTO>> {
    return this.get("/resources", params);
  }

  getResourceBySlug(
    slug: string
  ): Promise<PublicDetailResponse<PublicResourceDetailDTO>> {
    return this.get(`/resources/${encodeURIComponent(slug)}`);
  }

  getNoticias(
    params: GetNoticiasParams = {}
  ): Promise<PublicListResponse<PublicNoticiaDTO>> {
    return this.get("/noticias", params);
  }

  getNoticiaBySlug(
    slug: string
  ): Promise<PublicDetailResponse<PublicNoticiaDetailDTO>> {
    return this.get(`/noticias/${encodeURIComponent(slug)}`);
  }
}

export default CmsPublicApiClient;
